export type MostRecentOrder = {
  orderNumber?: number;
  orderStatus?: string;
  orderItemCount?: number;
};

export type StatusBarFlags = {
  orderNumberEncrypted?: string;
  showStatusBar?: boolean;
  showMostRecentOrder?: boolean;
  showAbandonCartMessage?: boolean;
};

export type AutoReorders = {
  id?: number;
  name?: string;
  desiredOrderDate?: string;
};

export type RepImage = {
  imagePath: string;
};

export type RepInfo = {
  name?: string;
  phone?: string;
  email?: string;
  images: RepImage[];
};

export type Platinum = {
  enabled: boolean;
  notifications: Notification[];
  repInfo: RepInfo;
};

export type Notification = {
  dateModified: string;
  message: string;
  dateCreated: string;
  dateDeleted: string;
  link: string;
  id: number;
  type: string;
  isDeletableByUser: number;
};

type Country = {
  canShip?: boolean;
  countryCode?: string;
  countryName?: string;
  showHeaderCountryFlag?: boolean;
};

export type AnalyticsData = {
  /* eslint-disable camelcase */
  uq_customer_id?: string;
  hashedUserEmailSha256?: string;
  platinum_id?: string;
  company_type?: string;
  web_plus?: string;
  date_registered?: string;
  purchaser_role_title?: string;
  amount_spent?: string;
  is_internal?: string;
  is_on_vpn?: string;
  is_registered?: string;
  total_orders?: string;
  meals_served?: string;
  cuisine_type?: string;
  number_of_seats?: string;
  payment_type?: string;
  ab_test_bucket?: string;
  variationIds?: string;
  ip?: string;
  emailTrafficHashedId?: string;
  cart_total?: number;
  logged_in_as_user?: string;
  /* eslint-enable camelcase */
};

type FacebookUserData = {
  ct?: string;
  country?: string;
  em?: string;
  fn?: string;
  ln?: string;
  ph?: string;
  st?: string;
  zp?: string;
};

type Wishlist = {
  listName?: string;
  num?: number;
};

export type SamplePurchaseHistory = {
  itemNumberId: string;
  totalQuantity: number;
};

export type WebPlusHomePageAd = {
  webPlusAdId: number;
  adEndDate: string;
};

export type PreviouslyPurchasedItems = {
  [itemNumber: string]: string;
};

export type User = {
  index?: string;
  guid?: string;
  firstName?: string;
  fullName?: string;
  companyName?: string;
  hasWebPlus?: boolean;
  invoices?: boolean;
  itemsInCart?: number;
  orderGuides?: boolean;
  orderGuideEnabled?: boolean;
  pendingOrders?: number;
  quotes?: boolean;
  registered?: boolean;
  showNextag?: boolean;
  showRapidReorder?: boolean;
  token?: string;
  platinum: Platinum;
  country: Country;
  wishLists: Wishlist[];
  emailAddress?: string;
  autoReorders: AutoReorders;
  hasPurchaseHistory?: boolean;
  analyticsData: AnalyticsData;
  billingPhoneNumber?: string;
  billingAddressLineTwo?: string;
  billingCountry?: string;
  facebookUserData: FacebookUserData;
  supervisorIndex?: string;
  csrfToken?: string;
  amountSpentOneThousandOrMore?: boolean;
  amountSpentFiveThousandOrMore?: boolean;
  isCommercial?: boolean;
  isRewardsMember?: boolean;
  billingAddress?: string;
  billingCity?: string;
  billingStateProvince?: string;
  billingPostalCode?: string;
  canQuickCheckout?: boolean;
  isTextNotificationsEnabled?: boolean;
  requiresSupervisorApproval?: boolean;
  /**
   * @deprecated - This property will soon be deprecated. Please migrate to sessionId instead.
   *
   * Remarks: CFID could be either ColdFusion sessions, which are a different
   * format than a GUID, or Storefront sessions (GUIDs). SessionId will consistently be
   * a Storefront session id. Please consult a team lead or architect if you are unsure if your
   * area relies upon CFID specifically
   */
  cfid?: string;
  sessionId?: string;
  cartCode?: string;
  isSupervisor?: boolean;
  isSubordinate?: boolean;
  lastGetDynamicDataFetch?: number;
  skipSettingGDDFetch?: boolean;
  isVersaHubEligible?: boolean;
  isUsingUnsupportedBrowser?: boolean;
  samplePurchaseHistory?: SamplePurchaseHistory[];
  webPlusHomePageAdIds?: WebPlusHomePageAd[];
  previouslyPurchasedItems?: PreviouslyPurchasedItems;
  freeviewPromotion?: FreeviewPromotionType;
  freeviewEndDate?: string;
  gpcEnabled: boolean;
  isLoggedIn?: boolean;
  totalSpentPastYear: number;
};

export type FreeviewPromotionType = 0 | 1 | 2 | 3 | 4;

export const FreeviewPromotionTypeStr = {
  NotFreeview: 0,
  HighVolumeAd1: 1,
  HighVolumeAd2: 2,
  SporadicAd1: 3,
  SporadicAd2: 4,
} as const;
